import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Icon, Row, Col, Container, Collapsible,
} from '@openedx/paragon';
import { Error } from '@openedx/paragon/icons';
import InactivityOptOutToggle from './cmp-components/InactivityOptOutToggle';

// modify
import {
  getSavingStatuses,
  getCompanyManagementPortalGeneralData,
  getSelectedCompanyDetails,
  getSelectedCompanyOrPlatformKey,
} from '../../data/selectors';
// modify
import {
  setSelectedCompanyOrPlatformKey,
  fetchCompanyEmployeesSuccess,
  fetchCompanyAdminsSuccess,
} from '../../data/slice';
// modify
import {
  createNewCompany,
  fetchCompanyEmployees,
  fetchCompanyAdmins,
  fetchRemainingCompanyEmployeeEnrollments,
} from '../../data/thunks';
import { LoadingSpinner } from '../../../generic/Loading';
import { logStudioHomeError } from '../../logging';
import AlertMessage from '../../../generic/alert-message';
import messages from '../messages';
import CompanySelector from './cmp-components/CompanySelector';
import CompanyDetails from './cmp-components/CompanyDetails';
import LicenseManagement from './cmp-components/LicenseManagement';
import ManageLearners from './cmp-components/ManageLearners';
import AdminPortalCachedData from './cmp-components/AdminPortalCachedData';
import LearnerManagementTable from './cmp-components/LearnerManagementTable';
import './index.scss';

interface Company {
  company_name: string;
  friendly_name: string;
  edx_course_org?: string;
  size: string;
  website?: string;
  admins: string[];
  primary_admin_email: string;
  custom_csv_fields: string[];
  custom_admin_portal: string[];
}

interface Props {
  isLoading: boolean;
  isFailed: boolean;
}

const CompanyManagementPortalTab: React.FC<Props> = ({
  isLoading,
  isFailed,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // State
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyDetailLoading, setCompanyDetailLoading] = useState(false);
  const [employeeListLoading, setEmployeeListLoading] = useState(false);

  // Collapsible state for each section (managed here, passed down as props)
  const [isCompanyDetailsOpen, setIsCompanyDetailsOpen] = useState(true);
  const [isManageLearnersOpen, setIsManageLearnersOpen] = useState(true);
  const [isLicenseManagementOpen, setIsLicenseManagementOpen] = useState(true);
  const [isEmailCampaignOpen, setIsEmailCampaignOpen] = useState(true);
  const [isAdminPortalCacheOpen, setIsAdminPortalCacheOpen] = useState(true);

  // Redux selectors
  const companyManagementData = useSelector(
    getCompanyManagementPortalGeneralData,
  );
  const selectedCompanyDetails = useSelector(getSelectedCompanyDetails);
  const savingStates = useSelector(getSavingStatuses);
  const sharedSelectionKey = useSelector(getSelectedCompanyOrPlatformKey);

  const companies: Company[] = Array.isArray(companyManagementData)
    ? companyManagementData
    : [];
  const isCreatingCompany = savingStates.createCompanySavingStatus === 'pending';

  const handleCompanySelect = (company: Company | null) => {
    if (!company) {
      setSelectedCompany(null);
      dispatch(setSelectedCompanyOrPlatformKey(null));
      return;
    }

    setSelectedCompany(company);
    setCompanyDetailLoading(true);
    dispatch(fetchCompanyEmployeesSuccess([]));
    dispatch(fetchCompanyAdminsSuccess([]));
    dispatch(setSelectedCompanyOrPlatformKey(company.company_name));
  };

  // On mount or when companies load, check if there's a shared selection to restore
  useEffect(() => {
    if (sharedSelectionKey && companies.length > 0 && !selectedCompany) {
      const matchingCompany = companies.find(
        (c) => c.company_name === sharedSelectionKey,
      );
      if (matchingCompany) {
        handleCompanySelect(matchingCompany);
      }
    }
  }, [sharedSelectionKey, companies.length]);

  // Sync changes from other tabs (e.g., Programs tab)
  useEffect(() => {
    if (
      sharedSelectionKey
      && selectedCompany?.company_name !== sharedSelectionKey
    ) {
      const matchingCompany = companies.find(
        (c) => c.company_name === sharedSelectionKey,
      );
      if (matchingCompany) {
        handleCompanySelect(matchingCompany);
      } else if (sharedSelectionKey === null) {
        // Selection was cleared in another tab
        setSelectedCompany(null);
      }
    }
  }, [sharedSelectionKey]);

  // Sync Redux state with local state for selected company details.
  // Also clears companyDetailLoading once the hoisted fetch in TabsSection completes.
  useEffect(() => {
    if (selectedCompanyDetails && selectedCompany) {
      // Only update if the company names match to prevent overriding during selection
      if (
        selectedCompanyDetails.company_name === selectedCompany.company_name
      ) {
        setSelectedCompany(selectedCompanyDetails);
        setCompanyDetailLoading(false);
      }
    }
  }, [selectedCompanyDetails, selectedCompany?.company_name]);

  // Fetch the first employee page and its enrollments before continuing through the rest
  // of the roster in the background.
  useEffect(() => {
    const fetchEmployeeList = async () => {
      if (selectedCompany?.company_name) {
        setEmployeeListLoading(true);
        try {
          const employeePage = await dispatch(fetchCompanyEmployees(selectedCompany.company_name));
          dispatch(fetchCompanyAdmins());
          if (employeePage?.nextPage) {
            dispatch(fetchRemainingCompanyEmployeeEnrollments(
              selectedCompany.company_name,
              employeePage.nextPage,
            ));
          }
        } catch (error) {
          logStudioHomeError('cmp_company_employees_fetch_failed', error);
        } finally {
          setEmployeeListLoading(false);
        }
      }
    };

    fetchEmployeeList();
  }, [selectedCompany?.company_name, dispatch]);

  const handleCompanyCreate = async (companyData: Partial<Company>) => {
    try {
      const result = await dispatch(createNewCompany(companyData));
      const newCompany = result as unknown as Company;
      // Route through handleCompanySelect so the shared Redux key is updated
      // and cross-tab selection (e.g. Programs tab) stays in sync.
      handleCompanySelect(newCompany);
      return newCompany;
    } catch (error) {
      logStudioHomeError('cmp_company_create_failed', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <Row className="m-0 mt-4 justify-content-center">
        <LoadingSpinner />
      </Row>
    );
  }

  return isFailed ? (
    <AlertMessage
      variant="danger"
      description={(
        <Row className="m-0 align-items-center">
          <Icon src={Error} className="text-danger-500 mr-1" />
          <span data-testid="error-failed-message">
            {intl.formatMessage(messages.companyManagementPortalMessage)}
          </span>
        </Row>
      )}
    />
  ) : (
    <Container fluid className="company-management-portal-container py-4">
      <Row>
        <Col xs={12}>
          <h2 className="mb-4">Company Management Portal</h2>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="mb-4">
          <CompanySelector
            companies={companies}
            selectedCompany={selectedCompany}
            onCompanySelect={handleCompanySelect}
            onCompanyCreate={handleCompanyCreate}
            isCreatingCompany={isCreatingCompany}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="mb-4">
          <Collapsible open={isCompanyDetailsOpen} onToggle={setIsCompanyDetailsOpen} title={<h4 className="mb-0">Company Details</h4>}>
            <CompanyDetails
              company={selectedCompany}
              isLoading={companyDetailLoading}
            />
          </Collapsible>
        </Col>
      </Row>
      {/* Admin Portal Cached Data - Show under Company Details when no company selected */}
      {!selectedCompany && (
        <Row>
          <Col xs={12} className="mb-4">
            <Collapsible open={isAdminPortalCacheOpen} onToggle={setIsAdminPortalCacheOpen} title={<h4 className="mb-0">Admin Portal Cached Data</h4>}>
              <AdminPortalCachedData />
            </Collapsible>
          </Col>
        </Row>
      )}

      {selectedCompany && (
        <>
          {/* DEBUG: License Data Sources - REMOVE AFTER TESTING */}
          {/* <Row>
              <Col xs={12} className="mb-3">
                <Alert variant="info" className="border-primary">
                  <Alert.Heading className="fs-6 mb-2">🔍 DEBUG: License Data Sources (Remove Later)</Alert.Heading>
                  <div><strong>Company:</strong> {selectedCompany.company_name}</div>
                  <div className="mt-2">
                    <strong>Legacy Bundles:</strong>{' '}
                    {selectedCompanyLicenses?.legacyBundles?.length > 0 ? (
                      <span className="text-success">
                        ✅ Found {selectedCompanyLicenses.legacyBundles.length} bundle(s)
                      </span>
                    ) : (
                      <span className="text-muted">❌ None found</span>
                    )}
                  </div>
                  <div>
                    <strong>Company Catalog:</strong>{' '}
                    {selectedCompanyLicenses?.catalog && Object.keys(selectedCompanyLicenses.catalog).length > 0 ? (
                      <span className="text-success">
                        ✅ Found {Object.keys(selectedCompanyLicenses.catalog).length} course(s)
                      </span>
                    ) : (
                      <span className="text-muted">❌ None found</span>
                    )}
                  </div>
                  <div>
                    <strong>Programs/Pathways:</strong>{' '}
                    {selectedCompanyLicenses?.programs?.length > 0 ? (
                      <span className="text-success">
                        ✅ Found {selectedCompanyLicenses.programs.length} program(s)
                      </span>
                    ) : (
                      <span className="text-muted">❌ None found</span>
                    )}
                  </div>
                  <div>
                    <strong>Skill Select:</strong>{' '}
                    {selectedCompanyLicenses?.skillSelect ? (
                      <span className="text-success">
                        ✅ Configured (enabled: {selectedCompanyLicenses.skillSelect.enabled ? 'Yes' : 'No'})
                      </span>
                    ) : (
                      <span className="text-muted">❌ Not configured</span>
                    )}
                  </div>
                </Alert>
              </Col>
            </Row> */}

          {/* Learner Management Section */}
          <Row>
            <Col xs={12} className="mb-4">
              <Collapsible open={isManageLearnersOpen} onToggle={setIsManageLearnersOpen} title={<h4 className="mb-0">Manage Learners</h4>}>
                <ManageLearners />

                <LearnerManagementTable
                  companyName={selectedCompany?.company_name || null}
                  isLoading={employeeListLoading}
                />
              </Collapsible>
            </Col>
          </Row>

          <Row>
            <Col xs={12} className="mb-4">
              <Collapsible open={isLicenseManagementOpen} onToggle={setIsLicenseManagementOpen} title={<h4 className="mb-0">License Management</h4>}>
                <LicenseManagement company={selectedCompany} />
              </Collapsible>
            </Col>
          </Row>

          {/* Email Campaign Configuration */}
          <Row>
            <Col xs={12} className="mb-4">
              <Collapsible open={isEmailCampaignOpen} onToggle={setIsEmailCampaignOpen} title={<h4 className="mb-0">Email Campaign Configuration</h4>}>
                <div className="p-3">
                  <p className="text-muted small mb-3">
                    Configure automated email campaign settings for this
                    organization.
                  </p>
                  <InactivityOptOutToggle
                    orgSlug={selectedCompany.company_name}
                    orgName={
                      selectedCompany.friendly_name
                      || selectedCompany.company_name
                    }
                  />
                </div>
              </Collapsible>
            </Col>
          </Row>

          {/* Admin Portal Cached Data */}
          <Row>
            <Col xs={12} className="mb-4">
              <Collapsible open={isAdminPortalCacheOpen} onToggle={setIsAdminPortalCacheOpen} title={<h4 className="mb-0">Admin Portal Cached Data</h4>}>
                <AdminPortalCachedData
                  companyName={selectedCompany.company_name}
                  companyFriendlyName={selectedCompany.friendly_name}
                />
              </Collapsible>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default CompanyManagementPortalTab;
